import { useState, useEffect } from 'react';
import { useRoute, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Layout } from '@/components/layout/Layout';
import { CheckoutForm } from '@/components/payments/CheckoutForm';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiRequest } from '@/lib/api';
import { format } from 'date-fns';
import { ArrowLeft, Calendar, MapPin, Users, CreditCard, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BookingSummary } from '@/components/checkout/BookingSummary';

// Make sure to call loadStripe outside of a component's render to avoid
// recreating the Stripe object on every render.
// This is your test publishable API key.
if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
  throw new Error('Missing VITE_STRIPE_PUBLIC_KEY environment variable');
}
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

type BookingPaymentProps = {
  bookingId?: number;
};

export default function BookingPayment({ bookingId: propBookingId }: BookingPaymentProps) {
  const [_, params] = useRoute('/booking-payment/:id');
  // Use the prop bookingId if provided, otherwise fallback to route params
  const bookingId = propBookingId || (params?.id ? parseInt(params.id) : null);
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  // Get booking details
  const { data: booking, isLoading: isBookingLoading } = useQuery({
    queryKey: ['bookings', bookingId],
    queryFn: async () => {
      if (!bookingId) return null;
      const res = await apiRequest('GET', `bookings/${bookingId}`);
      // if (!res.ok) throw new Error('Failed to fetch booking');
      // return res.json();
      return res;
    },
    enabled: !!bookingId,
  });
  
  // Get property details for the booking
  const { data: property, isLoading: isPropertyLoading } = useQuery({
    queryKey: ['properties', booking?.propertyId],
    queryFn: async () => {
      if (!booking?.propertyId) return null;
      const res = await apiRequest('GET', `properties/${booking.propertyId}`);
      // if (!res.ok) throw new Error('Failed to fetch property');
      // return res.json();
      return res;
    },
    enabled: !!booking?.propertyId,
  });
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return `AED ${(amount / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  // Get client secret from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const secret = urlParams.get('clientSecret');
    if (secret) {
      setClientSecret(secret);
    }
  }, []);
  
  // Check for payment_intent in URL params (for redirect back from Stripe)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentIntentParam = urlParams.get('payment_intent');
    const paymentIntentStatus = urlParams.get('redirect_status');
    
    if (paymentIntentParam && paymentIntentStatus === 'succeeded') {
      setPaymentStatus('success');
      // Confirm the booking
      apiRequest('POST', `bookings/${bookingId}/confirm`, { status: 'CONFIRMED' })
        .then(() => {
          toast({
            title: 'Payment Successful',
            description: 'Your booking has been confirmed!',
          });
        })
        .catch((error) => {
          console.error('Error confirming booking:', error);
          toast({
            title: 'Error',
            description: 'Payment successful but failed to confirm booking. Please contact support.',
            variant: 'destructive',
          });
        });
    } else if (paymentIntentParam && paymentIntentStatus === 'failed') {
      setPaymentStatus('failed');
      toast({
        title: 'Payment Failed',
        description: 'There was an issue with your payment. Please try again.',
        variant: 'destructive',
      });
    }
  }, [bookingId, toast]);
  
  // Handle payment success
  const handlePaymentSuccess = () => {
    setPaymentStatus('success');
    toast({
      title: 'Payment Successful',
      description: 'Your booking has been confirmed!',
    });
  };
  
  // Handle payment error
  const handlePaymentError = (error: Error) => {
    setPaymentStatus('failed');
    toast({
      title: 'Payment Failed',
      description: error.message || 'There was an issue with your payment',
      variant: 'destructive',
    });
  };
  
  // Go to bookings page
  const goToBookings = () => {
    setIsRedirecting(true);
    setTimeout(() => {
      setLocation('/bookings');
    }, 1000);
  };
  
  // Loading state
  if (isBookingLoading || isPropertyLoading) {
    return (
      <Layout>
        <div className="container max-w-4xl py-12">
          <div className="flex justify-center items-center min-h-[50vh]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading booking details...</span>
          </div>
        </div>
      </Layout>
    );
  }
  
  // No booking found
  if (!booking || !property) {
    return (
      <Layout>
        <div className="container max-w-4xl py-12">
          <Card>
            <CardHeader>
              <CardTitle>Booking Not Found</CardTitle>
              <CardDescription>We couldn't find the booking you're looking for</CardDescription>
            </CardHeader>
            <CardContent>
              <p>The booking you're trying to pay for doesn't exist or you don't have access to it.</p>
            </CardContent>
            <CardFooter>
              <Button onClick={() => setLocation('/properties')}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Properties
              </Button>
            </CardFooter>
          </Card>
        </div>
      </Layout>
    );
  }
  
  // Payment success
  if (paymentStatus === 'success') {
    return (
      <Layout>
        <div className="container max-w-4xl py-12">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 bg-green-100 w-16 h-16 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Payment Successful!</CardTitle>
              <CardDescription>Your booking has been confirmed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 border rounded-lg">
                <div className="flex gap-4 items-center mb-4">
                  {property.images && property.images[0] ? (
                    <img 
                      src={property.images[0].imageUrl} 
                      alt={property.title} 
                      className="w-24 h-24 rounded-md object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-muted rounded-md flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-lg">{property.title}</h3>
                    <p className="text-sm text-muted-foreground">{property.area}, {property.address}</p>
                    <Badge variant="outline" className="mt-1">
                      Booking #{booking.id}
                    </Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 mr-2 text-primary" />
                    <div>
                      <p className="text-sm font-medium">Check-in</p>
                      <p className="text-sm">{format(new Date(booking.checkIn), 'MMM d, yyyy')}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 mr-2 text-primary" />
                    <div>
                      <p className="text-sm font-medium">Check-out</p>
                      <p className="text-sm">{format(new Date(booking.checkOut), 'MMM d, yyyy')}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Users className="h-5 w-5 mr-2 text-primary" />
                    <div>
                      <p className="text-sm font-medium">Guests</p>
                      <p className="text-sm">{booking.guestCount} {booking.guestCount === 1 ? 'guest' : 'guests'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm">Base price</span>
                    <span className="text-sm">{formatCurrency(booking.baseAmount)}</span>
                  </div>
                  
                  {booking.markupAmount > 0 && (
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">Service fee</span>
                      <span className="text-sm">{formatCurrency(booking.markupAmount)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between mb-1">
                    <span className="text-sm">Platform fee</span>
                    <span className="text-sm">{formatCurrency(booking.platformFeeAmount)}</span>
                  </div>
                  
                  <div className="flex justify-between font-semibold border-t pt-2 mt-2">
                    <span>Total paid</span>
                    <span>{formatCurrency(booking.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="justify-center">
              <Button onClick={goToBookings} disabled={isRedirecting}>
                {isRedirecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  <>View My Bookings</>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </Layout>
    );
  }
  
  // Payment failed
  if (paymentStatus === 'failed') {
    return (
      <Layout>
        <div className="container max-w-4xl py-12">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 bg-red-100 w-16 h-16 rounded-full flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl">Payment Failed</CardTitle>
              <CardDescription>There was an issue processing your payment</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-center">Please try again or use a different payment method.</p>
            </CardContent>
            <CardFooter className="justify-center space-x-4">
              <Button variant="outline" onClick={() => setLocation('/properties')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Properties
              </Button>
              <Button onClick={() => setPaymentStatus('pending')}>
                <CreditCard className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </CardFooter>
          </Card>
        </div>
      </Layout>
    );
  }
  
  // Payment pending - show payment form
  return (
    <Layout>
      <div className="container max-w-4xl py-12">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => setLocation('/properties')} className="mb-2">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Properties
          </Button>
          <h1 className="text-2xl font-bold">Complete Your Booking</h1>
          <p className="text-muted-foreground">Please provide your payment details to confirm your reservation</p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Payment Details</CardTitle>
                <CardDescription>Secure payment via Stripe</CardDescription>
              </CardHeader>
              <CardContent>
                {clientSecret ? (
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <CheckoutForm 
                      bookingId={bookingId || 0} 
                      returnUrl={`/bookings/${bookingId}/confirmation`}
                      totalAmount={booking?.totalAmount || 0}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentError={handlePaymentError}
                    />
                  </Elements>
                ) : (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2">Initializing payment...</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Booking Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3 items-center">
                    {property.images && property.images[0] ? (
                      <img 
                        src={property.images[0].imageUrl} 
                        alt={property.title} 
                        className="w-16 h-16 rounded-md object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-sm">{property.title}</h3>
                      <p className="text-xs text-muted-foreground">{property.area}</p>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-primary" />
                      <div className="flex justify-between w-full">
                        <span className="text-sm">Check-in</span>
                        <span className="text-sm font-medium">{format(new Date(booking.checkIn), 'MMM d, yyyy')}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-primary" />
                      <div className="flex justify-between w-full">
                        <span className="text-sm">Check-out</span>
                        <span className="text-sm font-medium">{format(new Date(booking.checkOut), 'MMM d, yyyy')}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-2 text-primary" />
                      <div className="flex justify-between w-full">
                        <span className="text-sm">Guests</span>
                        <span className="text-sm font-medium">{booking.guestCount}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm">Base price</span>
                      <span className="text-sm">{formatCurrency(booking.baseAmount)}</span>
                    </div>
                    
                    {booking.markupAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm">Service fee</span>
                        <span className="text-sm">{formatCurrency(booking.markupAmount)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between">
                      <span className="text-sm">Platform fee</span>
                      <span className="text-sm">{formatCurrency(booking.platformFeeAmount)}</span>
                    </div>
                    
                    <div className="border-t mt-2 pt-2 flex justify-between font-medium">
                      <span>Total</span>
                      <span>{formatCurrency(booking.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="mt-4 text-sm text-muted-foreground">
              <p>By proceeding with this booking, you agree to our <a href="/terms" className="underline">Terms and Conditions</a> and <a href="/privacy" className="underline">Privacy Policy</a>.</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}